import React, { useEffect, useMemo, useState } from 'react';

import { fetchStaffList } from '../../../services/api/staffApi';
import useDepartmentStore from '../../../stores/departmentStore';
import { selectAssigneeCandidates } from './assigneeCandidates';

/**
 * AssigneePicker — compact avatar grid used inside the half-disc widget.
 *
 * UX:
 *   - Self avatar always rendered first and selected by default.
 *   - Up to 4 colleagues with EXECUTE_SITTING-eligible roles (DOCTOR /
 *     NURSE) shown next, ranked by recency where possible.
 *   - "More…" reveals a scrollable list of every eligible member when
 *     the team is larger than the inline grid.
 *   - Avatar = first letter of name in a coloured circle. Selected
 *     avatar gets a teal ring.
 *
 * A2-S6 — department scoping: the candidate list is filtered to staff in
 * the VISIT'S stamped department (department of care) OR staff with NO
 * department (org-wide, visible in every dept's picker). Same rule as the
 * reassign picker + registration; single-sourced through
 * `filterProvidersByDept` + the shared `deptsByMember` map. Degrades to the
 * full role-eligible list when the visit has no/emergency department or the
 * org has no departments (visitDepartmentId falsy → no filter). This is a
 * DISPLAY filter only; the server re-validates on submit, so un-hydrated
 * edges fail open.
 *
 * Props:
 *   value              — selected member_id ('' means "self")
 *   onChange(id)       — fires with the new selected member_id (or viewer.memberId for self)
 *   viewer             — { memberId, role }
 *   palette            — disc theme tokens
 *   visitDepartmentId  — the visit's stamped department_id (null/'' → no dept filter)
 */

const ROLE_LABEL = {
  DOCTOR: 'Doctor',
  NURSE: 'Nurse',
  RECEPTIONIST: 'Receptionist',
  ADMIN: 'Admin',
  OTHER_STAFF: 'Staff',
};

function displayName(member) {
  if (!member) return 'Unknown';
  const first = (member.first_name || '').trim();
  const last = (member.last_name || '').trim();
  const composed = `${first} ${last}`.trim();
  if (composed) return composed;
  return member.full_name || member.name || member.email || 'Unknown';
}

function roleLabel(member) {
  if (!member) return '';
  const raw = (member.role || '').toUpperCase();
  if (raw === 'OTHER_STAFF' && member.role_name) return member.role_name;
  return ROLE_LABEL[raw] || raw || '';
}

function initials(name) {
  if (!name) return '?';
  const parts = String(name).trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0][0].toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

function colourFor(memberId) {
  // Stable hash → hue. Same member always gets the same colour so the
  // doctor learns to recognise their team by avatar tint.
  let h = 0;
  const s = String(memberId || '');
  for (let i = 0; i < s.length; i += 1) {
    h = (h * 31 + s.charCodeAt(i)) >>> 0;
  }
  const hue = h % 360;
  return `hsl(${hue}, 55%, 55%)`;
}

export default function AssigneePicker({ value, onChange, viewer, palette, visitDepartmentId = null }) {
  const [staff, setStaff] = useState([]);
  const [showAll, setShowAll] = useState(false);
  const [loaded, setLoaded] = useState(false);

  // A2-S6 — member→dept map for the dept scope filter. Read from the shared
  // departmentStore (single source of truth) and bulk-hydrate on mount so the
  // candidates are filtered by the visit's department of care. hydrateAllMemberDepts
  // is cost-aware (one fetch per DEPARTMENT, not per member) and no-ops for orgs
  // without departments. Skipped entirely when there's no dept to filter by.
  const deptsByMember = useDepartmentStore((s) => s.deptsByMember);
  const hydrateAllMemberDepts = useDepartmentStore((s) => s.hydrateAllMemberDepts);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const r = await fetchStaffList();
      if (!cancelled) {
        setStaff(r?.success ? (r.data || []) : []);
        setLoaded(true);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    // Only hydrate when a department gate is actually in effect; org-wide /
    // emergency visits (falsy dept) need no map. Display-only — fail-open on
    // un-hydrated edges, so a hydrate miss never hides a candidate.
    if (visitDepartmentId) hydrateAllMemberDepts?.();
  }, [visitDepartmentId, hydrateAllMemberDepts]);

  const eligible = useMemo(() => {
    return selectAssigneeCandidates(staff, {
      viewerMemberId: viewer?.memberId,
      visitDepartmentId,
      deptsByMember,
    });
  }, [staff, viewer?.memberId, visitDepartmentId, deptsByMember]);

  const inline = eligible.slice(0, 4);
  const overflow = eligible.length > inline.length;
  const selectedId = value || viewer?.memberId || '';

  const renderAvatar = (member, isSelf = false) => {
    const id = isSelf ? viewer?.memberId : member.member_id;
    const name = isSelf ? 'You' : displayName(member);
    const role = isSelf ? '' : roleLabel(member);
    const isSelected = selectedId === id;
    const tint = isSelf ? '#0d9488' : colourFor(id);
    const tooltip = isSelf
      ? 'Assign to yourself'
      : (role ? `${name} · ${role}` : name);
    return (
      <button
        key={id || 'self'}
        type="button"
        onClick={() => onChange?.(id)}
        title={tooltip}
        aria-label={`Assign to ${isSelf ? 'yourself' : name}${role ? `, ${role}` : ''}`}
        aria-pressed={isSelected}
        style={{
          width: 32,
          height: 32,
          borderRadius: '50%',
          background: tint,
          color: '#fff',
          fontSize: '0.75rem',
          fontWeight: 700,
          border: isSelected ? '2px solid #0d9488' : `2px solid transparent`,
          boxShadow: isSelected ? '0 0 0 2px #fff inset' : 'none',
          cursor: 'pointer',
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}
      >
        {isSelf ? 'Y' : initials(name)}
      </button>
    );
  };

  return (
    <div>
      <label style={{ fontSize: '0.6875rem', opacity: 0.85, display: 'block', marginBottom: 4 }}>
        Assign to
      </label>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
        {renderAvatar(null, true)}
        {inline.map((m) => renderAvatar(m))}
        {overflow && !showAll && (
          <button
            type="button"
            onClick={() => setShowAll(true)}
            className="px-2 py-1 rounded-md border bg-white text-slate-700 hover:bg-slate-100
                       dark:bg-slate-800 dark:text-slate-100 dark:hover:bg-slate-700 dark:border-slate-600"
            style={{ fontSize: '0.6875rem', borderColor: palette.border }}
          >
            More…
          </button>
        )}
      </div>
      {showAll && (
        <div
          style={{
            marginTop: 6,
            padding: 6,
            border: `1px solid ${palette.border}`,
            borderRadius: 8,
            background: palette.bgAlt,
            maxHeight: 160,
            overflowY: 'auto',
            display: 'flex',
            flexDirection: 'column',
            gap: 4,
          }}
        >
          {eligible.slice(4).map((m) => {
            const name = displayName(m);
            const role = roleLabel(m);
            const isSelected = selectedId === m.member_id;
            return (
              <button
                key={m.member_id}
                type="button"
                onClick={() => { onChange?.(m.member_id); setShowAll(false); }}
                title={role ? `${name} · ${role}` : name}
                className="w-full text-left rounded-md hover:bg-slate-100 dark:hover:bg-slate-700"
                style={{
                  padding: '0.3rem 0.5rem',
                  fontSize: '0.75rem',
                  color: palette.text,
                  background: isSelected ? 'rgba(13,148,136,0.12)' : 'transparent',
                  border: 'none',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                }}
              >
                <span
                  aria-hidden="true"
                  style={{
                    width: 22, height: 22, borderRadius: '50%',
                    background: colourFor(m.member_id), color: '#fff',
                    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: '0.625rem', fontWeight: 700, flexShrink: 0,
                  }}
                >
                  {initials(name)}
                </span>
                <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {name}
                </span>
                {role && (
                  <span style={{ fontSize: '0.625rem', opacity: 0.7 }}>{role}</span>
                )}
              </button>
            );
          })}
          {eligible.length <= 4 && loaded && (
            <p style={{ margin: 0, fontSize: '0.6875rem', opacity: 0.7 }}>No more team members.</p>
          )}
        </div>
      )}
    </div>
  );
}
